/* ============================================================
   Super Subscriptions — v47
   Full replacement for super-subscriptions-loyalty.js

   Dynamic offer discount; one native Add to Cart button.
   Existing layout, gift ordering and images retained.
   ============================================================ */

(() => {
  "use strict";

  const VERSION = "v47";

  /* Listing cover images: blank keeps each gift's existing image. */
  const GIFT_COVER_IMAGES = {
    digestiveEnzymes: "",
    digestionTracker: "",
    welcomeGift: "",
    prioritySupport: "",
    telegramVIP: ""
  };

  /* Presentation only; does not change which gifts checkout includes. */
  const FIRST_BOX_COPY = [
    {
      key: "digestiveEnzymes",
      match: /digestive\s+enzymes?/i,
      title: "Digestive Enzymes"
    },
    {
      key: "digestionTracker",
      match: /(?:ai.*digestion|digestion.*tracker)/i,
      title: "6 Mths AI Digestion Tracker",
      worth: "Worth S$120"
    },
    {
      key: "welcomeGift",
      match: /welcome\s+gift/i,
      title: "Welcome Gift",
      worth: "Worth S$30"
    },
    {
      key: "prioritySupport",
      match: /priority\s+support/i,
      title: "24/7 Priority Support",
      worth: ""
    },
    {
      key: "telegramVIP",
      match: /telegram/i,
      title: "Telegram VIP Channel",
      worth: ""
    }
  ];

  const ROOT = ".super-simple-subs-wrapper";
  const PLAN = ".sss-aurora-option.subscription-option";
  const OWN = "[data-ssfg-v34]";
  const STYLE_ID = "ssfg-v34-styles";

  const records = new Map();
  const requests = new Map();
  const formButtons = new Map();

  let observer;
  let timer;
  let busy = false;

  const clean = value =>
    String(value ?? "").replace(/\s+/g, " ").trim();

  const text = node => clean(node?.textContent);

  const numericId = value =>
    String(value ?? "").match(/(\d+)$/)?.[1] || "";

  function make(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  }

  function own(node, record) {
    node.setAttribute("data-ssfg-v34", "");
    record.nodes.push(node);
    return node;
  }

  function money(cents, sample = "") {
    const prefix =
      clean(sample).match(/^(?:SGD|S\$|USD|US\$|AUD|A\$|HKD|MYR|RM|\$|€|£)\s*/i)?.[0]
      || "$";

    return prefix + (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function urlOf(value) {
    const source = typeof value === "string" ? value : value?.src;
    if (!source) return "";

    try {
      const url = new URL(source, location.href);
      return /^https?:$/.test(url.protocol) ? url.href : "";
    } catch (_) {
      return "";
    }
  }

  function imageOf(product) {
    return urlOf(product?.featured_image) ||
      urlOf(product?.image) ||
      urlOf(product?.images?.[0]);
  }

  function titleOf(value) {
    return clean(value)
      .replace(/\s*[-—–]\s*Default Title\s*$/i, "")
      .replace(/\s*[-—–]\s*Free Gift\s*$/i, "")
      .replace(/\s*\(one-time\)\s*$/i, "")
      .trim();
  }

  function keyOf(value) {
    return titleOf(value).toLowerCase()
      .replace(/[^a-z0-9]+/g, " ").trim();
  }

  async function json(url) {
    if (!requests.has(url)) {
      requests.set(
        url,
        fetch(url, { credentials: "same-origin" })
          .then(response => response.ok ? response.json() : null)
          .catch(() => null)
      );
    }

    return requests.get(url);
  }

  async function productJSON(href) {
    try {
      const url = new URL(href, location.href);
      if (url.origin !== location.origin) return null;
      if (!url.pathname.includes("/products/")) return null;

      url.search = "";
      url.hash = "";
      url.pathname = url.pathname.replace(/\/$/, "")
        .replace(/\.js$/, "") + ".js";

      return await json(url.href);
    } catch (_) {
      return null;
    }
  }

  function hide(node, record) {
    if (!node || node.closest(OWN)) return;
    if (record.hidden.some(entry => entry.node === node)) return;
    if (node.querySelector("input,select,button")) return;

    record.hidden.push({
      node,
      display: node.style.getPropertyValue("display"),
      priority: node.style.getPropertyPriority("display"),
      aria: node.getAttribute("aria-hidden")
    });

    node.style.setProperty("display", "none", "important");
    node.setAttribute("aria-hidden", "true");
  }

  function restore(record) {
    record.hidden.forEach(entry => {
      if (entry.display) {
        entry.node.style.setProperty(
          "display", entry.display, entry.priority
        );
      } else {
        entry.node.style.removeProperty("display");
      }

      if (entry.aria === null) {
        entry.node.removeAttribute("aria-hidden");
      } else {
        entry.node.setAttribute("aria-hidden", entry.aria);
      }
    });

    record.nodes.forEach(node => node.remove());
    record.nodes = [];
    record.hidden = [];
  }

  /*
   * Read the matching product variant and selling plan.
   * Do not use another product or plan as a fallback.
   */
  function context(card, root) {
    const variantId = numericId(
      card.getAttribute("product-variant-id")
    );
    const planId = numericId(
      card.getAttribute("sss-selling-plan-id")
    );

    const instanceId = root.getAttribute("data-sss-instance");
    const product =
      window.$superSubscriptions?.instances?.[instanceId]?.product;

    const variant = product?.variants?.find(
      item => String(item.id) === variantId
    );

    const plan = product?.selling_plan_groups
      ?.flatMap(group => group.selling_plans || [])
      .find(item => String(item.id) === planId);

    const labels = window.sssPriceLabels?.[variantId];
    const planLabels = labels?.[planId];

    const settings = Object.entries(
      window.sssubscriptionsSettings?.selling_plans || {}
    ).find(([key]) => numericId(key) === planId)?.[1];

    const bulk = planLabels?.bulkQuantity || settings?.bulkQuantity;
    const quantity = bulk?.enabled &&
      Number.isInteger(Number(bulk.value)) &&
      Number(bulk.value) > 0
        ? Number(bulk.value) : 0;

    const allocation = variant?.selling_plan_allocations?.find(
      item => String(item.selling_plan_id) === planId
    );

    const onlineUnit = Number(labels?.price?.number ?? variant?.price);
    const nativePay = Number(planLabels?.price?.number);

    const pay = Number.isFinite(nativePay)
      ? nativePay
      : quantity && Number.isFinite(Number(allocation?.price))
        ? Number(allocation.price) * quantity : null;

    const online = quantity && Number.isFinite(onlineUnit)
      ? onlineUnit * quantity : null;

    /*
     * Read encoded billing and delivery intervals.
     * Do not derive months from bottle count or approximate days.
     */
    const optionValues = (plan?.options || [])
      .map(option => option.value || "").join(" ");

    const billingMatch = optionValues.match(
      /(?:^|[,\s])B-(DAY|WEEK|MONTH|YEAR)_(\d+)/i
    );
    const deliveryMatch = optionValues.match(
      /(?:^|[,\s])D-(DAY|WEEK|MONTH|YEAR)_(\d+)/i
    );

    const interval = match => match ? {
      unit: match[1].toLowerCase(),
      count: Number(match[2])
    } : null;

    const billing = interval(billingMatch);
    const delivery = interval(deliveryMatch) || billing;
    const sample = planLabels?.price?.withoutCurrency ||
      labels?.price?.withoutCurrency || "";

    return {
      variantId, planId, product, variant, plan,
      labels, planLabels, settings, quantity,
      pay, online, billing, delivery, sample,
      saving: online !== null && pay !== null
        ? Math.max(0, online - pay) : null
    };
  }

  function intervalLabel(interval) {
    if (!interval) return "";
    return `${interval.count} ${interval.unit}${
      interval.count === 1 ? "" : "s"
    }`;
  }

  function nativeOrder(loyalty, index) {
    if (!loyalty) return "";

    const row = [...loyalty.querySelectorAll("[data-lb-reward-idx]")]
      .find(node =>
        Number(node.getAttribute("data-lb-reward-idx")) === index
      );

    return text(row).match(/\bOrder\s*#?\s*\d+\b/i)?.[0] || "";
  }

  function giftData(ctx, loyalty) {
    const configuration = ctx.planLabels?.planGift ||
      ctx.settings?.planGift;

    /*
     * Only replace an automatic "all gifts" presentation.
     * A gift chooser must retain its native selection controls.
     */
    const automatic = configuration?.giftMode === "all";

    const gifts = automatic
      ? (configuration.gifts || [])
          .filter(gift => gift.recurrence === "first_order")
          .map(gift => ({
            id: numericId(gift.variantId),
            title: titleOf(gift.title),
            image: urlOf(gift.imageUrl),
            quantity: Math.max(1, Number(gift.quantity) || 1),
            value: null,
            future: false,
            timing: ""
          }))
      : [];

    const flow = ctx.settings?.journeyLoyaltyFlow;
    const rewards = [];

    if (flow?.enabled) {
      (flow.rewards || []).forEach((reward, index) => {
        if (reward.enabled === false) return;

        const timing = nativeOrder(loyalty, index) ||
          (flow.type === "cycle" && Number(reward.cycle) > 0
            ? `Cycle ${reward.cycle}` : "");

        (reward.actions || []).forEach(action => {
          if (action.type !== "FREE_PRODUCT") return;

          const id = numericId(action.productVariantId);
          if (!id) return;

          rewards.push({
            id,
            title: titleOf(action.productTitle),
            image: "",
            quantity: 1,
            value: null,
            future: true,
            timing,
            conditional: Boolean(reward.conditions?.length)
          });
        });
      });
    }

    return { gifts, rewards, automatic };
  }

  async function resolveGift(gift) {
    if (!gift.id) return;

    const variant = await json(
      `/variants/${encodeURIComponent(gift.id)}.js`
    );

    if (variant) {
      const amount = Number(variant.price);
      if (Number.isFinite(amount) && amount > 0) {
        gift.value = Math.round(amount);
      }

      if (!gift.image) {
        gift.image = urlOf(variant.featured_image);
      }
    }

    if (gift.image && gift.value !== null) return;

    /*
     * Search only locates a product URL.
     * The product must contain the exact configured variant ID.
     */
    const params = new URLSearchParams({
      q: gift.title,
      "resources[type]": "product",
      "resources[limit]": "10",
      "resources[options][unavailable_products]": "show"
    });

    const results = await json(`/search/suggest.json?${params}`);
    const products = results?.resources?.results?.products || [];

    const candidates = products.filter(product =>
      keyOf(product.title) === keyOf(gift.title)
    );

    for (const candidate of candidates) {
      if (!candidate.url) continue;
      const product = await productJSON(candidate.url);

      const selected = product?.variants?.find(
        item => String(item.id) === gift.id
      );

      if (!selected) continue;

      const amount = Number(selected.price);
      if (Number.isFinite(amount) && amount > 0) {
        gift.value = Math.round(amount);
      }

      if (!gift.image) {
        gift.image = urlOf(selected.featured_image) ||
          imageOf(product);
      }

      break;
    }
  }

  function tile(gift, sample) {
    const node = make("div", "ssfg45-tile");

    if (gift.image) {
      const picture = make("div", "ssfg45-image");
      const image = document.createElement("img");

      image.src = gift.image;
      image.alt = gift.title;
      image.loading = "lazy";
      image.width = 68;
      image.height = 68;

      image.addEventListener("error", () => {
        picture.remove();
      }, { once: true });

      picture.append(image);
      node.append(picture);
    }

    const name = make(
      "div", "ssfg45-name",
      gift.title.split(/\s+/).slice(0, 7).join(" ")
    );
    name.title = gift.title;

    const worth = make(
      "s", "ssfg45-worth",
      gift.value !== null
        ? `Worth ${money(gift.value * gift.quantity, sample)}`
        : ""
    );

    node.append(
      name,
      worth,
      make("div", "ssfg45-free", "FREE")
    );

    if (gift.timing) {
      node.append(make("small", "ssfg45-timing", gift.timing));
    }

    if (gift.conditional) {
      node.append(make(
        "small", "ssfg45-timing", "Subject to reward conditions"
      ));
    }

    return node;
  }

  function section(title, gifts, record, key, sample) {
    const section = make("section", "ssfg45-section");
    if (key === "loyalty") {
      section.classList.add("ssfg-loyalty-accordion");
      const style = document.createElement("style");
      style.textContent = `
        html body ${ROOT} .ssfg45-card .ssfg45-section.ssfg-loyalty-accordion {
          border:1px solid #d3d3d3!important;
          border-radius:8px!important;
          background:#fff!important;
          margin:8px 0!important;
          padding:0!important;
          overflow:hidden!important;
        }
        html body ${ROOT} .ssfg45-card .ssfg-loyalty-accordion > .ssfg45-heading {
          min-height:44px!important;
          margin:0!important;
          padding:12px!important;
          font-weight:700!important;
          background:#fff!important;
        }
        html body ${ROOT} .ssfg45-card .ssfg-loyalty-accordion > .ssfg45-heading > span:last-child {
          display:block!important;
          flex:0 0 8px!important;
          width:8px!important;
          height:8px!important;
          margin:-4px 3px 0 12px!important;
          border-right:2px solid currentColor!important;
          border-bottom:2px solid currentColor!important;
          transform:rotate(45deg)!important;
        }
        html body ${ROOT} .ssfg45-card .ssfg-loyalty-accordion > .ssfg45-heading[aria-expanded="true"] > span:last-child {
          transform:rotate(225deg)!important;
          margin-top:4px!important;
        }
        html body ${ROOT} .ssfg45-card .ssfg-loyalty-accordion > .ssfg45-grid:not([hidden]) {
          padding:0 12px 12px!important;
        }
      `;
      section.append(style);
    }
    const button = make("button", "ssfg45-heading");
    button.type = "button";

    const icon = make("span");
    icon.setAttribute("aria-hidden", "true");

    const grid = make("div", "ssfg45-grid");
    grid.style.setProperty(
      "--ssfg-cols", gifts.length === 6 ? "6" : "5"
    );

    const ordered = key === "gifts"
      ? gifts.map((gift, index) => ({
          gift, index,
          order: FIRST_BOX_COPY.findIndex(rule => rule.match.test(gift.title))
        })).sort((a, b) =>
          (a.order < 0 ? FIRST_BOX_COPY.length : a.order) -
          (b.order < 0 ? FIRST_BOX_COPY.length : b.order) || a.index - b.index
        ).map(entry => entry.gift)
      : gifts;

    ordered.forEach(gift => {
      const rule = key === "gifts"
        ? FIRST_BOX_COPY.find(item => item.match.test(gift.title))
        : null;
      const displayGift = rule ? {
        ...gift,
        title: rule.title,
        image: urlOf(GIFT_COVER_IMAGES[rule.key]) || gift.image
      } : gift;
      const item = tile(displayGift, sample);

      if (rule && Object.prototype.hasOwnProperty.call(rule, "worth")) {
        item.querySelector(".ssfg45-worth").textContent = rule.worth;
      }

      grid.append(item);
    });

    function apply() {
      grid.hidden = !record.open[key];
      button.setAttribute("aria-expanded", String(record.open[key]));
      icon.textContent = key === "loyalty" ? "" : (record.open[key] ? "−" : "+");
    }

    button.append(make("span", "", title), icon);

    button.addEventListener("click", event => {
      event.preventDefault();
      record.open[key] = !record.open[key];
      apply();
    });

    apply();
    section.append(button, grid);
    return section;
  }

  function summary(ctx, gifts) {
    const box = make("div", "ssfg45-summary");
    if (ctx.pay === null) return box;

    function row(label, value, strong = false) {
      const item = make(
        "div", "ssfg45-summary-row" + (strong ? " is-total" : "")
      );
      item.append(
        make("span", "", label),
        make(strong ? "strong" : "span", "", value)
      );
      box.append(item);
    }

    const priced = gifts.filter(gift => gift.value !== null);
    const giftTotal = priced.reduce(
      (sum, gift) => sum + gift.value * gift.quantity, 0
    );
    const missing = gifts.length - priced.length;

    if (ctx.saving !== null) {
      row("Subscription savings", money(ctx.saving, ctx.sample));
    }

    if (priced.length) {
      row("First-box gift value", money(giftTotal, ctx.sample));
    }

    if (ctx.saving !== null) {
      row(
        missing ? "Total value · priced items only" : "Total value",
        money(ctx.saving + giftTotal, ctx.sample),
        true
      );
    }

    row("You pay today", money(ctx.pay, ctx.sample), true);

    if (missing) {
      box.append(make(
        "small", "ssfg45-summary-note",
        "Unpriced gifts are included free but excluded from total value."
      ));
    }

    box.append(make(
      "small", "ssfg45-summary-note",
      "Total value means subscription savings plus first-box gift value. Future loyalty gifts are separate."
    ));

    return box;
  }

  /*
   * Integrated reference header.
   * Keeps the real subscription radio and native handlers.
   * Uses the subscription total, not a monthly-equivalent price.
   */
  function renderReferenceHeader(card, ctx, record) {
    const content = card.querySelector(".sss-aurora-option-content");
    const nativeName = card.querySelector(".sss-aurora-option-name");
    const nativePrice = card.querySelector(".sss-aurora-option-price-wrap");

    if (!content || !nativeName) return;

    const leaves = [...card.querySelectorAll("span,div,p,small,b,strong")]
      .filter(node =>
        !node.closest(OWN) &&
        !node.children.length
      );

    const popular = leaves.filter(node =>
      /^[★☆⭐\s]*MOST\s+POPULAR[★☆⭐\s]*$/i.test(text(node))
    );

    const discounts = leaves.filter(node =>
      /^\d+(?:\.\d+)?%\s*OFF(?:\s+FOR\s+LIFE)?[.!]?$/i.test(text(node))
    );

    /* Use this offer's native badge, never a fixed percentage.
     * Lifetime wording is retained only when the native offer supplies it.
     * The price-based fallback makes no recurring-duration claim.
     */
    const nativeDiscounts = [...new Set(discounts.map(text))];
    let discount = nativeDiscounts.length === 1 ? nativeDiscounts[0] : "";

    if (
      !discount &&
      Number.isFinite(ctx.online) && ctx.online > 0 &&
      Number.isFinite(ctx.pay) && ctx.pay >= 0 && ctx.pay < ctx.online
    ) {
      const percentage = (ctx.online - ctx.pay) / ctx.online * 100;
      const rounded = Math.round(percentage * 100) / 100;
      if (rounded > 0) discount = `${rounded}% OFF`;
    }

    /* Restore the user's approved lifetime copy; keep the offer's
     * percentage dynamic rather than hardcoding 20%.
     */
    if (discount) {
      discount = discount.replace(/[.!]+$/, "").trim();
      if (!/\bFOR\s+LIFE\b/i.test(discount)) {
        discount += " FOR LIFE";
      }
    }

    const interval = ctx.delivery || ctx.billing;
    const header = own(make("div", "ssfg-reference-header"), record);
    const copy = make("div", "ssfg-reference-copy");

    copy.append(
      make(
        "div",
        "ssfg-reference-title",
        interval
          ? `${interval.count}-${interval.unit.toUpperCase()} SUBSCRIPTION`
          : text(nativeName)
      )
    );

    const nativeRadio = card.querySelector("input.sss-selling-plan-radio");
    if (nativeRadio) {
      const title = copy.querySelector(".ssfg-reference-title");
      const titleRow = make("div", "ssfg45-selection-row");
      titleRow.style.cssText = "display:flex!important;align-items:center!important;gap:10px!important;width:100%!important;";
      const indicator = make("button", "ssfg45-selection");
      indicator.type = "button";
      indicator.setAttribute("role", "radio");
      indicator.setAttribute("aria-label", text(title));
      indicator.setAttribute("aria-checked", String(nativeRadio.checked));
      indicator.disabled = nativeRadio.disabled;
      indicator.style.cssText = "appearance:none!important;display:block!important;position:relative!important;flex:0 0 22px!important;width:22px!important;height:22px!important;min-width:22px!important;min-height:22px!important;padding:0!important;margin:0!important;border:2px solid var(--sss-aurora-color-1,#302d70)!important;border-radius:50%!important;box-shadow:none!important;cursor:pointer!important;";
      indicator.addEventListener("click", event => {
        event.preventDefault();
        event.stopPropagation();
        if (!nativeRadio.checked) nativeRadio.click();
        syncNativeButtons();
        schedule();
      });
      title.before(titleRow);
      titleRow.append(indicator, title);
    }

    if (discount) {
      copy.append(
        make("div", "ssfg-reference-discount", discount)
      );
    }

    const delivery = [];

    if (ctx.quantity) {
      delivery.push(
        `${ctx.quantity} ${ctx.quantity === 1 ? "bottle" : "bottles"}`
      );
    }

    if (interval) {
      delivery.push(`delivered every ${intervalLabel(interval)}`);
    }

    if (delivery.length) {
      copy.append(
        make("div", "ssfg-reference-delivery", delivery.join(", "))
      );
    }

    copy.append(
      make(
        "div",
        "ssfg-reference-note",
        "Pause, skip or cancel anytime."
      )
    );

    const prices = make("div", "ssfg-reference-prices");

    if (Number.isFinite(ctx.pay)) {
      if (
        Number.isFinite(ctx.online) &&
        ctx.online > ctx.pay
      ) {
        prices.append(
          make(
            "s",
            "ssfg-reference-original",
            money(ctx.online, ctx.sample)
          )
        );
      }

      prices.append(
        make(
          "strong",
          "ssfg-reference-current",
          money(ctx.pay, ctx.sample)
        )
      );
    } else if (nativePrice) {
      const fallback = nativePrice.cloneNode(true);

      fallback.removeAttribute("id");
      fallback.querySelectorAll("[id]").forEach(node =>
        node.removeAttribute("id")
      );

      prices.append(fallback);
    }

    header.append(copy, prices);

    if (
      !content.matches("input,select,button") &&
      !content.querySelector("input,select,button")
    ) {
      content.before(header);
      hide(content, record);
    } else {
      content.append(header);

      [
        nativeName,
        nativePrice,
        ...content.querySelectorAll(
          ".sss-aurora-benefits-list,.sss-aurora-inline-description"
        )
      ].forEach(node => {
        if (node && !node.contains(header)) hide(node, record);
      });
    }

    discounts.forEach(node => hide(node, record));
    popular.forEach(node => hide(node, record));

    if (
      popular.length ||
      /MOST\s+POPULAR/i.test(ctx.plan?.description || "")
    ) {
      card.prepend(
        own(
          make("div", "ssfg-reference-popular", "★ MOST POPULAR"),
          record
        )
      );
    }

    const style = own(document.createElement("style"), record);

    style.textContent = `
      html body ${ROOT} .ssfg45-card .sss-aurora-option-inner {
        align-items:flex-start!important;
        padding-top:18px!important;
        padding-bottom:12px!important;
        background:#f3f1eb!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-header {
        display:grid!important;
        grid-template-columns:minmax(0,1fr) max-content!important;
        align-items:start!important;
        gap:8px 12px!important;
        flex:1 1 0!important;
        min-width:0!important;
        width:100%!important;
        margin:0!important;
        padding:0!important;
        box-sizing:border-box!important;
        color:var(--sss-aurora-text-color,CanvasText)!important;
        text-align:left!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-copy {
        display:flex!important;
        flex-direction:column!important;
        align-items:flex-start!important;
        gap:0!important;
        min-width:0!important;
        margin:0!important;
        padding:0!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-title {
        display:block!important;
        margin:0!important;
        padding:0!important;
        font-size:16px!important;
        font-weight:800!important;
        line-height:1.25!important;
        text-transform:uppercase!important;
        overflow-wrap:normal!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-discount {
        display:table!important;
        width:auto!important;
        margin:6px 0 0!important;
        padding:2px 7px!important;
        border:1.5px solid #e3583e!important;
        border-radius:3px!important;
        background:transparent!important;
        color:#e3583e!important;
        -webkit-text-fill-color:#e3583e!important;
        font-size:12px!important;
        font-weight:800!important;
        line-height:1.25!important;
        text-transform:uppercase!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-delivery {
        display:block!important;
        margin:5px 0 0!important;
        padding:0!important;
        font-size:12px!important;
        font-weight:500!important;
        line-height:1.35!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-note {
        display:block!important;
        margin:3px 0 0!important;
        padding:0!important;
        font-size:11px!important;
        font-weight:400!important;
        line-height:1.35!important;
        opacity:.7!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-prices {
        display:flex!important;
        flex-direction:column!important;
        align-items:flex-end!important;
        justify-content:flex-start!important;
        gap:1px!important;
        margin:0!important;
        padding:0!important;
        text-align:right!important;
        white-space:nowrap!important;
        font-variant-numeric:tabular-nums!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-original {
        display:block!important;
        margin:0!important;
        padding:0!important;
        font-size:12px!important;
        font-weight:500!important;
        line-height:1.2!important;
        text-decoration:line-through!important;
        opacity:.55!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-current {
        display:block!important;
        margin:0!important;
        padding:0!important;
        font-size:22px!important;
        font-weight:800!important;
        line-height:1.15!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-popular {
        position:absolute!important;
        top:0!important;
        left:50%!important;
        transform:translate(-50%,-50%)!important;
        display:block!important;
        width:max-content!important;
        max-width:90%!important;
        margin:0!important;
        padding:4px 13px!important;
        border:0!important;
        border-radius:999px!important;
        background:#302d70!important;
        color:#fff!important;
        -webkit-text-fill-color:#fff!important;
        font-size:11px!important;
        font-weight:800!important;
        line-height:1.2!important;
        text-align:center!important;
        white-space:nowrap!important;
        pointer-events:none!important;
        z-index:5!important;
      }

      /* Theme-only correction. Arrangement and copy unchanged. */
      html body ${ROOT} .ssfg45-card {
        isolation:isolate!important;
        --sss-aurora-bg:#fff;
        --sss-aurora-selected-bg:#fff;
        background:#fff!important;
      }

      html body ${ROOT} .ssfg45-card .sss-aurora-option-inner,
      html body ${ROOT} .ssfg45-card .sss-aurora-option-content,
      html body ${ROOT} .ssfg45-card .ssfg45-widget,
      html body ${ROOT} .ssfg45-card .ssfg45-footer {
        background:#fff!important;
      }

      html body ${ROOT} .ssfg45-card .sss-aurora-option-inner {
        position:relative!important;
        z-index:0!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-popular {
        z-index:10!important;
        background:var(--sss-widget-aurora-discount-badge-background-color,
          var(--sss-aurora-color-1,CanvasText))!important;
        color:var(--sss-widget-aurora-discount-badge-text-color,#fff)!important;
        -webkit-text-fill-color:currentColor!important;
        font-weight:700!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-title,
      html body ${ROOT} .ssfg45-card .ssfg-reference-current {
        font-weight:700!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg-reference-discount {
        border-color:var(--sss-aurora-color-1,CanvasText)!important;
        color:var(--sss-aurora-color-1,CanvasText)!important;
        -webkit-text-fill-color:currentColor!important;
        font-weight:700!important;
      }

      @media(max-width:380px) {
        html body ${ROOT} .ssfg45-card .ssfg-reference-header {
          column-gap:8px!important;
        }

        html body ${ROOT} .ssfg45-card .ssfg-reference-title {
          font-size:14px!important;
        }

        html body ${ROOT} .ssfg45-card .ssfg-reference-current {
          font-size:20px!important;
        }

        html body ${ROOT} .ssfg45-card .ssfg-reference-delivery {
          font-size:11px!important;
        }
      }
    `;

    header.append(style);
  }

  function renderPrice(card, ctx, record) {
    if (ctx.pay === null) return;

    const native = card.querySelector(".sss-aurora-option-price-wrap");
    if (!native || native.closest(OWN)) return;

    const box = own(make("div", "ssfg45-price"), record);
    const months = ctx.billing?.unit === "month"
      ? ctx.billing.count
      : ctx.billing?.unit === "year"
        ? ctx.billing.count * 12 : 0;

    if (ctx.online !== null && ctx.online > ctx.pay) {
      box.append(make(
        "s", "ssfg45-price-original",
        money(months ? ctx.online / months : ctx.online, ctx.sample) +
          (months ? "/mo" : "")
      ));
    }

    box.append(make(
      "strong", "ssfg45-price-current",
      money(months ? ctx.pay / months : ctx.pay, ctx.sample) +
        (months ? "/mo" : "")
    ));

    if (ctx.billing) {
      box.append(make(
        "small", "ssfg45-billing",
        `${money(ctx.pay, ctx.sample)} billed every ${
          intervalLabel(ctx.billing)
        }`
      ));
    }

    native.before(box);
    hide(native, record);
  }

  function renderMeta(card, ctx, record) {
    const parts = [];

    if (ctx.quantity) {
      parts.push(
        `${ctx.quantity} ${ctx.quantity === 1 ? "bottle" : "bottles"}`
      );
    }

    if (ctx.delivery) {
      parts.push(`delivered every ${intervalLabel(ctx.delivery)}`);
    }

    if (!parts.length) return;

    const metadata = own(
      make("div", "ssfg45-meta", parts.join(", ")),
      record
    );

    const benefit = card.querySelector(
      ".sss-aurora-benefits-list,.sss-aurora-inline-description"
    );

    if (benefit) benefit.before(metadata);
    else {
      const content = card.querySelector(".sss-aurora-option-content");
      (content || card).append(metadata);
    }
  }

  function renderPopular(card, ctx, record) {
    const sources = [...card.querySelectorAll(
      "div,span,p,small,strong,b"
    )].filter(node =>
      !node.closest(OWN) &&
      !node.querySelector("input,select,button,a") &&
      /^[★☆⭐\s]*MOST\s+POPULAR[★☆⭐\s]*$/i.test(text(node))
    );

    if (
      !sources.length &&
      !/MOST\s+POPULAR/i.test(ctx.plan?.description || "")
    ) return;

    const badge = own(
      make("div", "ssfg45-pop", "MOST POPULAR"), record
    );

    card.prepend(badge);
    sources.forEach(node => hide(node, record));
  }

  function nativeButton(form) {
    return form?.querySelector(
      'button.gp-button-atc[name="add"][type="submit"]'
    );
  }

  function syncNativeButtons() {
    records.forEach((record, card) => {
      const selected = Boolean(
        card.querySelector("input.sss-selling-plan-radio")?.checked
      );
      const indicator = card.querySelector(".ssfg45-selection");
      if (indicator) {
        indicator.setAttribute("aria-checked", String(selected));
        indicator.style.setProperty("background", selected
          ? "radial-gradient(circle, var(--sss-aurora-color-1, #302d70) 0 45%, white 48%)"
          : "white", "important");
      }
    });

    const forms = new Set(
      [...records.keys()].map(card => card.closest("form")).filter(Boolean)
    );

    forms.forEach(form => {
      const native = nativeButton(form);
      if (!native) return;
      /* Do not replace, clone, click-proxy or disable the real button.
       * Super and the theme retain control of the selected purchase type.
       */
      if (native.classList.contains("ssfg45-native-hidden")) {
        native.classList.remove("ssfg45-native-hidden");
      }
    });
  }

  function renderCTA(card, root, ctx, record) {
    const form = root.closest("form");
    const native = nativeButton(form);
    if (!form || !native) return;

    /* One shared native CTA; no button is injected inside a plan.
     * Keep the native button in its existing theme location.
     */
    if (native.classList.contains("ssfg45-native-hidden")) {
      native.classList.remove("ssfg45-native-hidden");
    }

    /* Multiple subscription cards share one guarantee note per form. */
    if (form.querySelector(".ssfg45-guarantee")) return;

    const note = own(make("div", "ssfg45-guarantee"), record);
    note.style.cssText = "display:block!important;clear:both!important;width:100%!important;box-sizing:border-box!important;margin:0!important;padding:7px 0 0!important;font-family:inherit!important;font-size:10px!important;font-weight:400!important;line-height:1.4!important;text-align:center!important;";
    note.append(document.createTextNode("90-Day Guarantee • "));

    const policy = [...document.querySelectorAll("a[href]")]
      .find(link =>
        !link.closest(OWN) &&
        (
          /price beat guarantee/i.test(text(link)) ||
          /refund[-_]?policy/i.test(link.getAttribute("href") || "")
        )
      );

    if (policy) {
      const link = make("a", "", "Price Beat Guarantee");
      link.href = policy.href;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.cssText = "color:inherit!important;text-decoration:underline!important;";
      note.append(link);
    } else {
      note.append(document.createTextNode("Price Beat Guarantee"));
    }

    native.after(note);
  }

  function paint(card, root) {
    let record = records.get(card);

    if (!record) {
      record = {
        nodes: [],
        hidden: [],
        generation: 0,
        pending: false,
        open: { gifts: true, loyalty: false }
      };
      records.set(card, record);
    }

    restore(record);
    const generation = ++record.generation;
    const ctx = context(card, root);

    card.classList.add("ssfg45-card");

    const loyalty = [...root.querySelectorAll(".sss-loyalty-bonus")]
      .find(node =>
        numericId(node.getAttribute("sss-selling-plan-id")) === ctx.planId
      ) || card.querySelector(".sss-loyalty-bonus");

    const { gifts, rewards, automatic } = giftData(ctx, loyalty);

    renderReferenceHeader(card, ctx, record);

    const widget = own(make("div", "ssfg45-widget"), record);

    /*
     * Keep accordion interactions separate from card selection.
     */
    ["pointerdown", "mousedown", "touchstart", "click"].forEach(type => {
      widget.addEventListener(type, event => event.stopPropagation());
    });

    const draw = () => {
      const children = [];

      if (gifts.length) {
        children.push(section(
          "FREE WITH YOUR FIRST BOX",
          gifts, record, "gifts", ctx.sample
        ));
      }

      if (rewards.length) {
        children.push(section(
          "FUTURE LOYALTY GIFTS",
          rewards, record, "loyalty", ctx.sample
        ));
      }

      widget.replaceChildren(...children);
    };

    draw();
    card.append(widget);
    renderCTA(card, root, ctx, record);

    /*
     * Only hide automatic gift rows once replacements exist.
     * Preserve native gift selection controls.
     */
    if (automatic && gifts.length) {
      card.querySelectorAll(".sss-plan-gift,[data-plan-gift-row]")
        .forEach(node => {
          if (!node.closest(OWN)) hide(node, record);
        });
    }

    if (rewards.length && loyalty) {
      hide(loyalty, record);
    }

    Promise.all([...gifts, ...rewards].map(resolveGift))
      .then(() => {
        if (
          !card.isConnected ||
          !widget.isConnected ||
          record.generation !== generation
        ) return;

        draw();
      })
      .catch(error => console.warn("[SSFG] Gift resolution:", error));

    record.context = ctx;
    record.gifts = gifts;
    record.rewards = rewards;
  }

  const css = `
    html body ${ROOT} .ssfg45-card {
      position:relative!important;
      overflow:visible!important;
      background:var(--sss-aurora-bg,Canvas)!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      font-family:inherit!important;
      --sss-aurora-selected-bg:var(--sss-aurora-bg,Canvas);
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-option-inner,
    html body ${ROOT} .ssfg45-card .sss-aurora-option-content {
      background:var(--sss-aurora-bg,Canvas)!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-benefit-item,
    html body ${ROOT} .ssfg45-card .sss-aurora-benefit-item span,
    html body ${ROOT} .ssfg45-card .sss-aurora-inline-description,
    html body ${ROOT} .ssfg45-card .sss-aurora-option-name {
      color:var(--sss-aurora-text-color,CanvasText)!important;
      -webkit-text-fill-color:currentColor!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-shimmer {
      display:none!important;
    }

    html body ${ROOT} .ssfg45-pop {
      position:relative!important;
      display:table!important;
      width:auto!important;
      max-width:90%!important;
      margin:10px auto 0!important;
      padding:4px 12px!important;
      border-radius:20px!important;
      background:var(--sss-widget-aurora-discount-badge-background-color,
        var(--sss-aurora-color-1,CanvasText))!important;
      color:var(--sss-widget-aurora-discount-badge-text-color,Canvas)!important;
      font:800 10px/1.4 inherit;
      font-family:inherit!important;
      font-size:10px!important;
      font-weight:800!important;
      line-height:1.4!important;
      letter-spacing:.035em!important;
      text-align:center!important;
      pointer-events:none!important;
      z-index:2!important;
    }

    html body ${ROOT} .ssfg45-meta {
      display:block!important;
      margin:9px 0 10px!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      font:inherit!important;
      font-size:12px!important;
      font-weight:600!important;
      line-height:1.45!important;
      text-align:left!important;
    }

    html body ${ROOT} .ssfg45-price {
      display:flex!important;
      flex-direction:column!important;
      align-items:flex-end!important;
      gap:3px!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      font-family:inherit!important;
      text-align:right!important;
      font-variant-numeric:tabular-nums!important;
    }

    html body ${ROOT} .ssfg45-price-original {
      font-size:12px!important;
      line-height:1.3!important;
      opacity:.65!important;
      text-decoration:line-through!important;
    }

    html body ${ROOT} .ssfg45-price-current {
      font-size:20px!important;
      font-weight:800!important;
      line-height:1.2!important;
      white-space:nowrap!important;
    }

    html body ${ROOT} .ssfg45-billing {
      display:block!important;
      max-width:180px!important;
      font-size:10px!important;
      line-height:1.4!important;
    }

    html body ${ROOT} .ssfg45-widget,
    html body ${ROOT} .ssfg45-footer {
      display:block!important;
      clear:both!important;
      width:100%!important;
      box-sizing:border-box!important;
      background:var(--sss-aurora-bg,Canvas)!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      font-family:inherit!important;
      text-align:left!important;
      position:relative!important;
      z-index:2!important;
    }

    html body ${ROOT} .ssfg45-widget {
      padding:12px 14px 0!important;
      border-top:1px solid color-mix(in srgb,
        var(--sss-aurora-text-color,CanvasText) 14%,transparent)!important;
    }

    html body ${ROOT} .ssfg45-widget *,
    html body ${ROOT} .ssfg45-footer *,
    html body ${ROOT} .ssfg45-price * {
      box-sizing:border-box!important;
      font-family:inherit!important;
      -webkit-text-fill-color:currentColor!important;
    }

    html body ${ROOT} .ssfg45-section {
      display:block!important;
      margin:0!important;
      padding:0 0 12px!important;
      background:transparent!important;
    }

    html body ${ROOT} .ssfg45-section + .ssfg45-section {
      padding-top:10px!important;
      border-top:1px solid color-mix(in srgb,
        var(--sss-aurora-text-color,CanvasText) 14%,transparent)!important;
    }

    html body ${ROOT} .ssfg45-heading {
      appearance:none!important;
      display:flex!important;
      align-items:center!important;
      justify-content:space-between!important;
      gap:10px!important;
      width:100%!important;
      min-height:32px!important;
      margin:0 0 8px!important;
      padding:0!important;
      border:0!important;
      border-radius:0!important;
      background:transparent!important;
      box-shadow:none!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      font-size:11px!important;
      font-weight:800!important;
      line-height:1.4!important;
      text-align:left!important;
      cursor:pointer!important;
      touch-action:manipulation!important;
    }

    html body ${ROOT} .ssfg45-heading:focus-visible,
    html body ${ROOT} .ssfg45-cta:focus-visible {
      outline:2px solid var(--sss-aurora-color-1,CanvasText)!important;
      outline-offset:3px!important;
    }

    html body ${ROOT} .ssfg45-grid {
      display:grid!important;
      grid-template-columns:repeat(var(--ssfg-cols,5),minmax(0,1fr))!important;
      gap:12px 7px!important;
      align-items:start!important;
      width:100%!important;
      margin:0!important;
      padding:0!important;
    }

    html body ${ROOT} .ssfg45-grid[hidden] {
      display:none!important;
    }

    html body ${ROOT} .ssfg45-tile {
      display:flex!important;
      flex-direction:column!important;
      align-items:flex-start!important;
      min-width:0!important;
      margin:0!important;
      padding:0!important;
      color:var(--sss-aurora-text-color,CanvasText)!important;
      text-align:left!important;
    }

    html body ${ROOT} .ssfg45-image {
      display:block!important;
      width:100%!important;
      max-width:68px!important;
      aspect-ratio:1!important;
      margin:0!important;
      padding:0!important;
      overflow:hidden!important;
      background:transparent!important;
      border-radius:4px!important;
    }

    html body ${ROOT} .ssfg45-image img {
      display:block!important;
      width:100%!important;
      height:100%!important;
      max-width:100%!important;
      margin:0!important;
      object-fit:contain!important;
    }

    html body ${ROOT} .ssfg45-name {
      display:-webkit-box!important;
      -webkit-box-orient:vertical!important;
      -webkit-line-clamp:3!important;
      width:100%!important;
      height:3.9em!important;
      margin:6px 0 0!important;
      overflow:hidden!important;
      overflow-wrap:anywhere!important;
      font-size:10px!important;
      font-weight:600!important;
      line-height:1.3!important;
    }

    html body ${ROOT} .ssfg45-worth {
      display:block!important;
      min-height:26px!important;
      margin:4px 0 0!important;
      font-size:9px!important;
      line-height:13px!important;
      text-decoration:line-through!important;
      opacity:.75!important;
      overflow-wrap:anywhere!important;
    }

    html body ${ROOT} .ssfg45-free {
      display:block!important;
      margin:2px 0 0!important;
      color:var(--sss-aurora-color-1,CanvasText)!important;
      font-size:10px!important;
      font-weight:800!important;
      line-height:14px!important;
    }

    html body ${ROOT} .ssfg45-timing {
      display:block!important;
      margin-top:3px!important;
      font-size:9px!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-summary {
      display:block!important;
      padding:12px 0!important;
      border-top:1px solid color-mix(in srgb,
        var(--sss-aurora-text-color,CanvasText) 14%,transparent)!important;
    }

    html body ${ROOT} .ssfg45-summary-row {
      display:flex!important;
      align-items:baseline!important;
      justify-content:space-between!important;
      gap:12px!important;
      margin:0 0 6px!important;
      font-size:12px!important;
      line-height:1.45!important;
    }

    html body ${ROOT} .ssfg45-summary-row > :last-child {
      white-space:nowrap!important;
      font-variant-numeric:tabular-nums!important;
    }

    html body ${ROOT} .ssfg45-summary-row.is-total {
      font-weight:700!important;
    }

    html body ${ROOT} .ssfg45-summary-note {
      display:block!important;
      margin-top:5px!important;
      font-size:9px!important;
      line-height:1.4!important;
      opacity:.8!important;
    }

    html body ${ROOT} .ssfg45-footer {
      padding:0 14px 14px!important;
    }

    html body ${ROOT} .ssfg45-cta {
      appearance:none!important;
      display:block!important;
      width:100%!important;
      min-height:46px!important;
      margin:0!important;
      padding:12px 16px!important;
      border:0!important;
      border-radius:var(--sss-aurora-border-radius,8px)!important;
      background:var(--sss-widget-aurora-discount-badge-background-color,
        var(--sss-aurora-color-1,CanvasText))!important;
      color:var(--sss-widget-aurora-discount-badge-text-color,Canvas)!important;
      font-size:14px!important;
      font-weight:700!important;
      line-height:1.4!important;
      text-align:center!important;
      cursor:pointer!important;
    }

    html body ${ROOT} .ssfg45-cta:disabled {
      opacity:.6!important;
      cursor:wait!important;
    }

    html body ${ROOT} .ssfg45-guarantee {
      display:block!important;
      margin-top:9px!important;
      font-size:10px!important;
      line-height:1.5!important;
      text-align:center!important;
    }

    html body ${ROOT} .ssfg45-guarantee a {
      color:inherit!important;
      text-decoration:underline!important;
    }

    html body ${ROOT} .ssfg45-status {
      margin-top:6px!important;
      font-size:11px!important;
      line-height:1.4!important;
      text-align:center!important;
    }

    html body button.ssfg45-native-hidden {
      display:none!important;
    }

    /* Existing spacing polish. */
    html body ${ROOT} .ssfg45-meta {
      margin:6px 0 7px!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-benefits-list {
      gap:6px!important;
      margin-top:6px!important;
      margin-bottom:10px!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-benefit-item {
      margin-top:0!important;
      margin-bottom:0!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-section {
      padding-bottom:8px!important;
    }

    html body ${ROOT} .ssfg45-section + .ssfg45-section {
      padding-top:6px!important;
    }

    html body ${ROOT} .ssfg45-heading {
      margin-bottom:4px!important;
    }

    html body ${ROOT} .ssfg45-heading[aria-expanded="false"] {
      margin-bottom:0!important;
    }

    html body ${ROOT} .ssfg45-name {
      margin-top:4px!important;
      height:3.75em!important;
      line-height:1.25!important;
    }

    html body ${ROOT} .ssfg45-worth {
      min-height:13px!important;
      margin-top:2px!important;
    }

    html body ${ROOT} .ssfg45-summary {
      padding:9px 0!important;
    }

    html body ${ROOT} .ssfg45-summary-row {
      margin-bottom:4px!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-summary-note {
      margin-top:3px!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-guarantee {
      margin-top:7px!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-status:empty {
      margin-top:0!important;
    }

    @media(max-width:480px) {
      html body ${ROOT} .ssfg45-widget {
        padding:12px 10px 0!important;
      }

      html body ${ROOT} .ssfg45-footer {
        padding:0 10px 12px!important;
      }

      html body ${ROOT} .ssfg45-grid {
        gap:10px 5px!important;
      }

      html body ${ROOT} .ssfg45-name {
        font-size:9px!important;
      }

      html body ${ROOT} .ssfg45-price-current {
        font-size:18px!important;
      }

      html body ${ROOT} .ssfg45-billing {
        max-width:135px!important;
        font-size:9px!important;
      }
    }

    /* Shared horizontal alignment. */
    html body ${ROOT} .ssfg45-card {
      --ssfg45-inset:14px;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-option-inner {
      box-sizing:border-box!important;
      padding-left:var(--ssfg45-inset)!important;
      padding-right:var(--ssfg45-inset)!important;
      padding-bottom:12px!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-option-content {
      padding-left:0!important;
      padding-right:0!important;
      padding-bottom:0!important;
      margin-bottom:0!important;
    }

    html body ${ROOT} .ssfg45-meta {
      margin:8px 0!important;
      padding:0!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-benefits-list {
      gap:6px!important;
      margin:0!important;
      padding-top:0!important;
      padding-bottom:0!important;
    }

    html body ${ROOT} .ssfg45-card .sss-aurora-benefit-item {
      margin:0!important;
      padding-top:0!important;
      padding-bottom:0!important;
      line-height:1.35!important;
    }

    html body ${ROOT} .ssfg45-widget {
      margin:0!important;
      padding:10px var(--ssfg45-inset) 0!important;
    }

    html body ${ROOT} .ssfg45-section {
      padding:0 0 10px!important;
    }

    html body ${ROOT} .ssfg45-section + .ssfg45-section {
      padding-top:10px!important;
    }

    html body ${ROOT} .ssfg45-heading {
      min-height:32px!important;
      margin:0 0 6px!important;
      padding:0!important;
    }

    html body ${ROOT} .ssfg45-heading[aria-expanded="false"] {
      margin-bottom:0!important;
    }

    html body ${ROOT} .ssfg45-footer {
      margin:0!important;
      padding:0 var(--ssfg45-inset) 12px!important;
    }

    html body ${ROOT} .ssfg45-grid:not(:has(.ssfg45-worth:not(:empty))) .ssfg45-worth {
      display:none!important;
    }

    /* Align gift rows without fixed-height name gaps. */
    @supports (grid-template-rows:subgrid) {
      html body ${ROOT} .ssfg45-grid {
        row-gap:0!important;
        align-items:stretch!important;
      }

      html body ${ROOT} .ssfg45-tile {
        display:grid!important;
        grid-row:span 6!important;
        grid-template-rows:subgrid!important;
        grid-template-columns:minmax(0,1fr)!important;
        row-gap:0!important;
        align-items:start!important;
        align-content:start!important;
      }

      html body ${ROOT} .ssfg45-image {
        grid-row:1!important;
        grid-column:1!important;
      }

      html body ${ROOT} .ssfg45-name {
        grid-row:2!important;
        grid-column:1!important;
        height:auto!important;
        min-height:0!important;
        max-height:3.9em!important;
        margin:4px 0 0!important;
        padding:0!important;
        line-height:1.3!important;
      }

      html body ${ROOT} .ssfg45-worth {
        grid-row:3!important;
        grid-column:1!important;
        height:auto!important;
        min-height:0!important;
        margin:3px 0 0!important;
        padding:0!important;
        line-height:13px!important;
      }

      html body ${ROOT} .ssfg45-free {
        grid-row:4!important;
        grid-column:1!important;
        margin:3px 0 0!important;
        padding:0!important;
        line-height:14px!important;
      }

      html body ${ROOT} .ssfg45-timing {
        grid-row:5!important;
        grid-column:1!important;
        margin:3px 0 0!important;
      }

      html body ${ROOT} .ssfg45-timing + .ssfg45-timing {
        grid-row:6!important;
      }
    }

    @media(max-width:480px) {
      html body ${ROOT} .ssfg45-card {
        --ssfg45-inset:10px;
      }
    }
  `;

  function refresh() {
    if (busy) return;
    busy = true;
    observer?.disconnect();

    try {
      for (const [card, record] of records) {
        if (!card.isConnected) {
          restore(record);
          records.delete(card);
        }
      }

      document.querySelectorAll(ROOT).forEach(root => {
        root.querySelectorAll(PLAN).forEach(card => {
          try {
            paint(card, root);
          } catch (error) {
            const record = records.get(card);
            if (record) restore(record);
            console.warn("[SSFG] Native content restored:", error);
          }
        });
      });

      syncNativeButtons();
    } finally {
      busy = false;
      observer?.observe(document.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          "class",
          "checked",
          "value",
          "disabled",
          "product-variant-id",
          "sss-selling-plan-id"
        ]
      });
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  }

  function cleanup() {
    const classes = [
      "ssfg-cluster-hidden",
      "ssfg-native-gift-hidden",
      "ssfg-native-loyalty-hidden",
      "ssfg-native-pop-hidden",
      "ssfg-pill-source-hidden",
      "ssfg-description-source-hidden",
      "ssfg-native-discount-hidden",
      "super-subscriptions-free-gifts-native-hidden",
      "super-subscriptions-loyalty-integrated-hidden",
      "ssfg-v36-pop-source",
      "ssfg-v36-top-host",
      "ssfg43-card"
    ];

    document.querySelectorAll(ROOT).forEach(root => {
      root.querySelectorAll(
        `${OWN},.ssfg-kit,.ssfg-plan-meta,.ssfg-pop-badge,` +
        ".ssfg-pill,.ssfg-description-pill," +
        ".super-subscriptions-free-gifts"
      ).forEach(node => node.remove());

      classes.forEach(className => {
        root.querySelectorAll("." + className).forEach(node => {
          node.classList.remove(className);
          node.removeAttribute("aria-hidden");
        });
      });
    });

    document.querySelectorAll(".ssfg45-native-hidden")
      .forEach(node => node.classList.remove("ssfg45-native-hidden"));
  }

  function boot() {
    cleanup();

    let style = document.getElementById(STYLE_ID);

    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      document.head.append(style);
    }

    const siteFont = getComputedStyle(document.body).fontFamily;
    const siteButton = document.querySelector(
      'button.gp-button-atc[name="add"][type="submit"]'
    );
    const buttonStyle = siteButton ? getComputedStyle(siteButton) : null;

    style.textContent = css + `
      html body ${ROOT} .ssfg45-card,
      html body ${ROOT} .ssfg45-card * {
        font-family:${siteFont}!important;
        font-stretch:normal!important;
        font-style:normal!important;
        letter-spacing:normal!important;
      }

      html body ${ROOT} .ssfg45-card .sss-aurora-option-name {
        margin:0!important;
        font-size:16px!important;
        font-weight:600!important;
        line-height:1.25!important;
      }

      html body ${ROOT} .ssfg45-price {
        gap:2px!important;
      }

      html body ${ROOT} .ssfg45-price-current {
        font-size:20px!important;
        font-weight:700!important;
        line-height:1.2!important;
      }

      html body ${ROOT} .ssfg45-price-original {
        font-size:12px!important;
        font-weight:400!important;
        line-height:1.3!important;
      }

      html body ${ROOT} .ssfg45-card .ssfg45-meta,
      html body ${ROOT} .ssfg45-card .ssfg45-billing {
        margin:0!important;
        font-size:11px!important;
        font-weight:400!important;
        line-height:1.4!important;
      }

      html body ${ROOT} .ssfg45-header-details {
        margin:0!important;
        padding-top:3px!important;
        padding-bottom:8px!important;
        align-items:baseline!important;
        column-gap:10px!important;
      }

      html body ${ROOT} .ssfg45-card .sss-aurora-benefits-list {
        margin:0!important;
        padding-top:4px!important;
        padding-bottom:8px!important;
        row-gap:4px!important;
      }

      html body ${ROOT} .ssfg45-card .sss-aurora-benefit-item,
      html body ${ROOT} .ssfg45-card .sss-aurora-inline-description {
        font-size:11px!important;
        font-weight:400!important;
        line-height:1.4!important;
      }

      html body ${ROOT} .ssfg45-widget {
        margin:0!important;
        padding-top:8px!important;
      }

      html body ${ROOT} .ssfg45-section {
        margin:0!important;
        padding-bottom:8px!important;
      }

      html body ${ROOT} .ssfg45-section + .ssfg45-section {
        padding-top:8px!important;
      }

      html body ${ROOT} .ssfg45-heading {
        min-height:32px!important;
        margin:0!important;
        padding:0 0 6px!important;
        font-size:11px!important;
        font-weight:600!important;
        line-height:1.4!important;
      }

      html body ${ROOT} .ssfg45-heading[aria-expanded="false"] {
        padding-bottom:0!important;
      }

      html body ${ROOT} .ssfg45-name {
        height:auto!important;
        min-height:0!important;
        max-height:3.9em!important;
        margin:3px 0 0!important;
        font-size:10px!important;
        font-weight:500!important;
        line-height:1.3!important;
      }

      html body ${ROOT} .ssfg45-worth {
        min-height:0!important;
        margin:3px 0 0!important;
        font-size:9px!important;
        font-weight:400!important;
        line-height:1.35!important;
      }

      html body ${ROOT} .ssfg45-worth:empty {
        margin:0!important;
      }

      html body ${ROOT} .ssfg45-free {
        margin:2px 0 0!important;
        font-size:10px!important;
        font-weight:700!important;
        line-height:1.3!important;
      }

      @supports (grid-template-rows:subgrid) {
        html body ${ROOT} .ssfg45-worth {
          grid-row:2!important;
        }

        html body ${ROOT} .ssfg45-name {
          grid-row:3!important;
        }
      }

      html body ${ROOT} .ssfg45-pop {
        font-size:10px!important;
        font-weight:700!important;
        line-height:1.3!important;
      }

      html body ${ROOT} .ssfg45-cta {
        font-family:${buttonStyle?.fontFamily || siteFont}!important;
        font-size:${buttonStyle?.fontSize || "14px"}!important;
        font-weight:${buttonStyle?.fontWeight || "600"}!important;
        letter-spacing:${buttonStyle?.letterSpacing || "normal"}!important;
        border-radius:${buttonStyle?.borderRadius || "8px"}!important;
      }

      html body ${ROOT} .ssfg45-guarantee {
        margin:0!important;
        padding-top:7px!important;
        font-size:10px!important;
        font-weight:400!important;
        line-height:1.4!important;
      }
    `;

    document.documentElement.setAttribute("data-ssfg", VERSION);

    observer = new MutationObserver(mutations => {
      const relevant = mutations.some(mutation => {
        const target = mutation.target.nodeType === 1
          ? mutation.target : mutation.target.parentElement;

        if (target?.closest(OWN)) return false;

        if (target?.closest(ROOT)) return true;

        return [...mutation.addedNodes].some(node =>
          node.nodeType === 1 &&
          (node.matches?.(ROOT) || node.querySelector?.(ROOT))
        );
      });

      if (relevant) schedule();
    });

    document.addEventListener("change", event => {
      if (event.target.closest?.(ROOT)) schedule();
    });

    document.addEventListener("click", event => {
      if (
        event.target.closest?.(ROOT) &&
        !event.target.closest?.(OWN)
      ) schedule();
    });

    refresh();
    [500, 1500, 3500].forEach(delay => setTimeout(schedule, delay));

    window.ssfgDiagnose = () => {
      const report = {
        version: VERSION,
        plans: [...records].map(([card, record]) => ({
          connected: card.isConnected,
          variantId: record.context?.variantId,
          planId: record.context?.planId,
          bottles: record.context?.quantity,
          billing: record.context?.billing,
          onlineCents: record.context?.online,
          subscriptionCents: record.context?.pay,
          savingsCents: record.context?.saving,
          firstBoxGifts: record.gifts?.map(gift => ({
            variantId: gift.id,
            title: gift.title,
            imageResolved: Boolean(gift.image),
            onlinePriceCents: gift.value
          })),
          loyaltyGifts: record.rewards?.map(gift => ({
            variantId: gift.id,
            title: gift.title,
            timing: gift.timing,
            imageResolved: Boolean(gift.image),
            onlinePriceCents: gift.value
          }))
        }))
      };

      console.log("[SSFG]", report);
      return report;
    };

    console.info("[SSFG] Loaded:", VERSION);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot, { once: true });
  } else {
    boot();
  }
})();